import React, { useMemo, useCallback, useState } from 'react'
import {
  SaveButton,
  Toolbar,
  useRecordContext,
  useNotify,
  useRedirect,
  useDelete,
  Confirm,
} from 'react-admin'
import { Stack, Box, Button } from '@mui/material'
import { FormBottomToolbarWithPermissionProps, Services } from './type'
import { BackButton, SaveWithConfirmButton } from '../../buttons'
import DeleteIcon from '@mui/icons-material/Delete'

const CustomServiceToolbarWithPermission: React.FC<FormBottomToolbarWithPermissionProps> = ({
  children,
  hasConfirm = false,
  isCreate = false,
  rightButtons,
  encType,
  transform,
  label,
}) => {
  const confirmProps = useMemo(
    () => ({
      confirmTitle: isCreate ? 'ra.message.bulk_create_title' : 'ra.message.bulk_update_title',
      confirmContent: isCreate
        ? 'ra.message.bulk_create_content'
        : 'ra.message.bulk_update_content',
    }),
    [isCreate],
  )

  const DeleteButtonComponent = (): JSX.Element => {
    const [deletedata, setDeletdata] = useState({} as Services)
    const [open, setOpen] = useState(false)
    const handleDialogClose = (): void => setOpen(false)
    const record = useRecordContext()
    const notify = useNotify()
    const redirect = useRedirect()

    const onSuccess = useCallback(async (): Promise<void> => {
      notify('ra.notification.delete_service_success', { type: 'success' })
      redirect('/services')
    }, [notify, redirect])

    const onError = useCallback(
      (error): void => {
        if (error.response.data.code === '23503') {
          return notify(
            'ra.validation.delete_service_fail_by_key' || 'ra.validation.update_service_fail',
            { type: 'error' },
          )
        }
        if (error.response.data.code === 'FORBIDDEN') {
          return notify(
            'ra.auth.access_denied' || 'ra.validation.update_service_fail',
            { type: 'error' },
          )
        }
        return notify(error || 'ra.validation.update_service_fail', { type: 'error' })
      },
      [notify],
    )
    const [deleteOne] = useDelete(undefined, undefined, { onError, onSuccess })

    const handleConfirm = useCallback(async (): Promise<void> => {
      await deleteOne('services', { id: record.id, previousData: deletedata })
      handleDialogClose()
    }, [deleteOne, deletedata, record])

    const handleClick = (data): void => {
      const { /* access_branches: accessBranches,*/ ...services } = data
      // email.branch_ids = accessBranches
      setDeletdata(services as Services)
      //   deleteOne('services', { id: record.id, previousData: record })
      setOpen(true)
    }
    return (
      <Stack direction='row' gap={2}>
        {record ? (
          <Box>
            <Button
              onClick={handleClick}
              startIcon={<DeleteIcon />} // Replace 'add_circle' with your desired icon from the Material Icons library
            >
              ลบ
            </Button>
          </Box>
        ) : (
          <BackButton />
        )}
        <Confirm
          isOpen={open}
          title='คุณต้องการลบข้อมูลหรือไม่?'
          content='คุณต้องการลบข้อมูลหรือไม่?'
          onConfirm={handleConfirm}
          onClose={handleDialogClose}
        />
      </Stack>
    )
  }

  return (
    <Toolbar sx={{ justifyContent: 'space-between', py: 1.5 }}>
      <Stack flexGrow={1} spacing={1}>
        <Stack direction='row' justifyContent='space-between'>
          <DeleteButtonComponent />
          <Stack direction='row' justifyContent='space-between' gap={2}>
            {rightButtons}
            {hasConfirm ? (
              <SaveWithConfirmButton encType={encType} {...confirmProps} transform={transform} />
            ) : (
              <SaveButton size='medium' transform={transform} label={label} />
            )}
          </Stack>
        </Stack>
        {children}
      </Stack>
    </Toolbar>
  )
}

export default CustomServiceToolbarWithPermission
